import { css } from '@emotion/react';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Collapse, SvgIconProps, Typography, TypographyProps, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAtomValue } from 'jotai';
import { CSSProperties, FC, PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { isNavigationOpenAtom } from '../../state/NavigationState';
import { ShowChatBotAtom } from '../../../ChatBotSection/ChatBotSection';
import { MENU_WIDTH } from '../Navbar';
import { Breakpoint, ZINDEX } from '../../../../constants/styles';

export const ADDITIONAL_MENU_WIDTH = 320;

const WrapperStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100vh;
  background: linear-gradient(182.23deg, #7383cf 0%, #333389 50.61%);
  width: ${ADDITIONAL_MENU_WIDTH}px;
  backdrop-filter: blur(4px);
`;

const OverlayCollapseStyles = css`
  ${WrapperStyles};

  width: 0;
  transition:
    width 0.3s ease-in-out,
    transform 0.3s ease-in-out;

  &.open {
    width: ${ADDITIONAL_MENU_WIDTH}px;
  }

  &.chat-bot-open {
    @media screen and (max-width: ${Breakpoint.xLarge}px) {
      position: absolute;
      left: ${MENU_WIDTH}px;
      top: 0;
      z-index: ${ZINDEX.SIDE_NAVIGATION_OVERLAY};
      transform: translateX(-${ADDITIONAL_MENU_WIDTH + MENU_WIDTH}px);

      &.open {
        transform: translateX(0);
      }
    }
  }
`;

const ContentWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.5rem;
  width: 100%;
  padding: 15px 2rem 0 2rem;
  overflow: auto;
  padding-bottom: 1.5rem;
`;

const TitleWrapper = styled('div')`
  margin-bottom: 0.25rem;
`;

interface IProps {
  title: string;
  children: ReactNode;
}

export const NavigationWrapper: FC<IProps> = ({ title, children }) => {
  const isNavOpen = useAtomValue(isNavigationOpenAtom);
  const { secondary } = useTheme().colors;
  const isChatBotOpen = useAtomValue(ShowChatBotAtom);

  const classNames: string = useMemo(() => {
    const names: string[] = [];
    if (isNavOpen) {
      names.push('open');
    }
    if (isChatBotOpen) {
      names.push('chat-bot-open');
    }
    return names.join(' ');
  }, [isNavOpen, isChatBotOpen]);

  const innerContent = useMemo(() => {
    if (!isNavOpen) {
      return null;
    }
    return (
      <ContentWrapper>
        <TitleWrapper>
          <Typography variant={'h6'} color={secondary['10']}>
            {title}
          </Typography>
        </TitleWrapper>
        {children}
      </ContentWrapper>
    );
  }, [title, children, secondary, isNavOpen]);

  return (
    <div css={OverlayCollapseStyles} className={classNames}>
      {innerContent}
    </div>
  );
};

interface ICollapsibleViewSectionProps extends PropsWithChildren {
  title: string;
  defaultExpanded?: boolean;
}
export function CollapsibleViewSection({
  title,
  defaultExpanded = true,
  children,
}: ICollapsibleViewSectionProps) {
  const fontColor = useTheme().colors.secondary['10'];
  return (
    <CollapsibleSection
      title={title}
      iconPosition='end'
      defaultExpanded={defaultExpanded}
      iconProps={{ htmlColor: fontColor }}
      typographyProps={{ color: fontColor, variant: 'subtitle2' }}
    >
      {children}
    </CollapsibleSection>
  );
}

const collapsibleSectionStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
`;

interface ICollapsibleSectionProps extends PropsWithChildren {
  title: string;
  defaultExpanded?: boolean;
  iconPosition?: 'start' | 'end';
  style?: CSSProperties;
  iconProps?: SvgIconProps;
  typographyProps?: TypographyProps;
}
export function CollapsibleSection({
  title,
  iconPosition = 'end',
  defaultExpanded = true,
  children,
  style,
  iconProps,
  typographyProps,
}: ICollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const icon = useMemo(
    () => (
      <ChevronRightIcon
        sx={{
          transition: 'transform .1s',
          transform: `${expanded ? 'rotate(90deg)' : ''}`,
        }}
        {...iconProps}
      />
    ),
    [expanded, iconProps]
  );
  return (
    <Box>
      <div
        role='button'
        onClick={() => setExpanded((prev) => !prev)}
        css={collapsibleSectionStyles}
        style={style}
      >
        {iconPosition === 'start' && icon}
        <Typography {...typographyProps}>{title}</Typography>
        {iconPosition === 'end' && icon}
      </div>
      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  );
}
